import React from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { Button } from './Button';

// Helper function to generate the page numbers with ellipsis
const getPaginationRange = (currentPage: number, totalPages: number, siblings: number = 1): (number | string)[] => {
  const totalPageNumbers = siblings * 2 + 3; // Siblings on each side + current + first + last
  const totalBlocks = totalPageNumbers + 2; // With ellipsis

  if (totalPages <= totalBlocks) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSiblingIndex = Math.max(currentPage - siblings, 1);
  const rightSiblingIndex = Math.min(currentPage + siblings, totalPages);

  const shouldShowLeftEllipsis = leftSiblingIndex > 2;
  const shouldShowRightEllipsis = rightSiblingIndex < totalPages - 1;

  if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
    let leftItemCount = 3 + 2 * siblings;
    let leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
    return [...leftRange, '...', totalPages];
  }

  if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
    let rightItemCount = 3 + 2 * siblings;
    let rightRange = Array.from({ length: rightItemCount }, (_, i) => totalPages - rightItemCount + i + 1);
    return [1, '...', ...rightRange];
  }

  if (shouldShowLeftEllipsis && shouldShowRightEllipsis) {
    let middleRange = Array.from({ length: rightSiblingIndex - leftSiblingIndex + 1 }, (_, i) => leftSiblingIndex + i);
    return [1, '...', ...middleRange, '...', totalPages];
  }
  
  return []; // Fallback
};


interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange }: PaginationProps) {
  if (totalPages <= 1) {
    return null;
  }

  const pages = getPaginationRange(currentPage, totalPages);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(startItem + itemsPerPage - 1, totalItems);

  return (
    <div className="w-full flex flex-col md:flex-row items-center justify-between border-slate-200 dark:border-gray-700 mt-6">
      <div className="text-sm text-slate-500 dark:text-gray-400 mb-4 md:mb-0">
        Showing <span className="font-semibold text-slate-700 dark:text-gray-200">{startItem}</span> to <span className="font-semibold text-slate-700 dark:text-gray-200">{endItem}</span> of <span className="font-semibold text-slate-700 dark:text-gray-200">{totalItems}</span> results
      </div>
      <nav className="flex items-center gap-2">
        <Button 
          variant="secondary" 
          size="sm" 
          onClick={() => onPageChange(currentPage - 1)} 
          disabled={currentPage === 1}
        >
          <FiChevronLeft className="h-4 w-4" />
          <span className="hidden sm:inline ml-1">Previous</span>
        </Button>
        
        <div className="hidden md:flex items-center gap-2">
          {pages.map((page, index) => {
            if (typeof page === 'string') {
              return <span key={`ellipsis-${index}`} className="px-3 py-1 text-slate-500">...</span>;
            }
            return (
              <Button
                key={page}
                variant={currentPage === page ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => onPageChange(page)}
              >
                {page}
              </Button>
            );
          })}
        </div>

        <Button 
          variant="secondary" 
          size="sm" 
          onClick={() => onPageChange(currentPage + 1)} 
          disabled={currentPage === totalPages}
        >
           <span className="hidden sm:inline mr-1">Next</span>
          <FiChevronRight className="h-4 w-4" />
        </Button>
      </nav>
    </div>
  );
}